import { useCallback, useEffect, useMemo, useState } from 'react';
import { projectSnapshotDaySchema, type ProjectSnapshotDay } from '@forecastify/shared';
import type { MenuItem } from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../i18n';
import { useSession } from '../../session';
import { parseList } from '../permissions-hook';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C62.4 · Visszatekintés. One cut, shared by the bar and by two kebabs.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE STATE LIVES ABOVE THE BAR BECAUSE THE MOCKUP'S CONTROLS ARE NOT ALL ON IT.** Its
 * entry — *Visszatekintés — napi snapshot* — is an item in the DASHBOARD's kebab, and so is the
 * delta toggle and *Vissza a jelenbe*; the bar itself only appears once a cut is set. A bar that
 * owned the state could not be opened from a menu outside it, which is why the first rebuild put
 * an *Open* button on the bar and a picker inside it. That was the shape being wrong rather than a
 * detail: three controls ended up somewhere the mockup does not have them.
 *
 * ⚠️⚠️ **AND TWO TABS SHARE ONE CUT.** Dashboard and Pénzügy both travel; somebody who scrubs to
 * March on one and switches to the other must still be in March.
 */

export interface Lookback {
  /** The cut as an instant, or `null` for live. What every panel query carries. */
  readonly at: string | null;
  /** The cut as a wire date — what the bar prints and the day list checks against. */
  readonly onDate: string | null;
  readonly days: readonly ProjectSnapshotDay[];
  /** ⚠️ No snapshot day means the feature cannot be entered, and the menu item says so. */
  readonly canStart: boolean;
  readonly delta: boolean;
  readonly start: () => void;
  readonly end: () => void;
  readonly go: (onDate: string) => void;
  readonly step: (by: number) => void;
  readonly toggleDelta: () => void;
  readonly atFirst: boolean;
  readonly atLast: boolean;
}

/**
 * ⚠️⚠️ **A DAY BECOMES AN INSTANT AT ITS END, AND EVERY QUERY CARRIES THE INSTANT.** The cut is a
 * `timestamptz` comparison on the server; the day a reader means by *"the 14th"* is everything
 * that had happened by the end of the 14th. Converting anywhere but here would put a second
 * answer to that question in the product.
 */
function endOf(onDate: string): string {
  return `${onDate}T23:59:59.999Z`;
}

export function useLookback(projectId: string): Lookback {
  const session = useSession();
  const [days, setDays] = useState<readonly ProjectSnapshotDay[]>([]);
  const [onDate, setOnDate] = useState<string | null>(null);
  const [delta, setDelta] = useState(false);

  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request(`/projects/${projectId}/as-of/days`).catch(() => null);
      if (response?.ok !== true) return;
      const list = parseList(projectSnapshotDaySchema, await response.json());
      if (list !== null) setDays(list);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId, projectId]);

  const index = onDate === null ? -1 : days.findIndex((one) => one.onDate === onDate);

  const go = useCallback((next: string) => {
    setOnDate(next);
  }, []);

  const step = useCallback(
    (by: number) => {
      if (days.length === 0) return;
      const from = index < 0 ? days.length - 1 : index;
      const landed = days[Math.min(days.length - 1, Math.max(0, from + by))];
      if (landed !== undefined) setOnDate(landed.onDate);
    },
    [days, index],
  );

  const start = useCallback(() => {
    /* ⚠️ The LAST closed day, which is the mockup's own `ttStart`. Opening on the oldest snapshot
       would show a reader the project as it was months ago and read as the control being broken. */
    const last = days.at(-1);
    if (last !== undefined) setOnDate(last.onDate);
  }, [days]);

  const end = useCallback(() => {
    setOnDate(null);
    setDelta(false);
  }, []);

  const toggleDelta = useCallback(() => {
    setDelta((current) => !current);
  }, []);

  return {
    at: onDate === null ? null : endOf(onDate),
    onDate,
    days,
    canStart: days.length > 0,
    delta,
    start,
    end,
    go,
    step,
    toggleDelta,
    atFirst: index <= 0,
    atLast: index >= days.length - 1,
  };
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE KEBAB'S TIME-MACHINE ITEMS, AND THEY REPLACE THEMSELVES.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's kebab is two different menus. Live, it offers the way IN and lets you open the
 * contract; in the past it offers the way out, the delta, and the same open commands **disabled
 * with a reason** — *"Múltbeli állapotban zárolva"*. That last part is the half that is easy to
 * skip and the half that answers the reader's real question, which is whether anything they click
 * now will act on the date they are looking at.
 */
export function lookbackMenuItems(t: Translate, lookback: Lookback): MenuItem[] {
  if (lookback.onDate === null) {
    return [
      {
        label: t('tt.open'),
        icon: 'clock',
        description: t('tt.openHint'),
        disabled: !lookback.canStart,
        onSelect: lookback.start,
      },
    ];
  }

  return [
    {
      label: t('tt.back'),
      icon: 'clock',
      description: t('tt.backHint'),
      onSelect: lookback.end,
    },
    {
      label: t('tt.delta'),
      checked: lookback.delta,
      description: t('tt.deltaHint'),
      onSelect: lookback.toggleDelta,
    },
  ];
}

/**
 * ⚠️⚠️ **THE MOCKUP'S `TT_PLAN` LABELS, DERIVED FROM THE ACTION.** Its change log is hardcoded
 * prose — *"Feladat-becslések pontosítása"* — because it has no audit log. We do, so the server
 * sends the actions and the label is chosen here: rule 7 means no user-facing string is minted on
 * the server, and rule 8 means the set of actions is the one the audit log already uses.
 *
 * ⚠️ **An action with no label of its own falls back to the generic one** rather than printing
 * `lineitem.reorder` at somebody. The marker is still drawn — the day DID change — and the
 * sentence is the honest vague one.
 */
const CHANGE_LABEL: Readonly<Record<string, MessageKey>> = {
  'task.create': 'tt.change.taskCreate',
  'task.update': 'tt.change.taskEstimate',
  'task.delete': 'tt.change.taskDelete',
  'lineitem.create': 'tt.change.lineItemCreate',
  'lineitem.update': 'tt.change.lineItemUpdate',
  'lineitem.delete': 'tt.change.lineItemDelete',
  'contract.create': 'tt.change.contractCreate',
  'contract.update': 'tt.change.contractUpdate',
  'contract.delete': 'tt.change.contractDelete',
  'allocation.create': 'tt.change.allocation',
  'allocation.update': 'tt.change.allocation',
  'allocation.delete': 'tt.change.allocation',
};

/** The one-line description under a day that carries plan changes. */
export function changeLabel(t: Translate, actions: readonly string[]): string | null {
  if (actions.length === 0) return null;
  const said = new Set<string>();
  for (const action of actions) {
    const key = CHANGE_LABEL[action];
    said.add(key === undefined ? t('tt.change.other') : t(key));
  }
  return [...said].join(' · ');
}

/**
 * ⚠️ **The date list, month by month, newest first** — the mockup's own ordering, because somebody
 * opening it is far more often going back a few days than a few months. Each month gets a heading
 * row, which is what makes a list of two hundred days navigable at all.
 */
export function useSnapshotMenu(
  lookback: Lookback,
  hint: (day: ProjectSnapshotDay) => string,
): MenuItem[] {
  const t = useT();

  return useMemo(() => {
    const months = t('calendar.months').split(',');
    const items: MenuItem[] = [];
    let month = '';

    for (const day of [...lookback.days].reverse()) {
      const key = day.onDate.slice(0, 7);
      if (key !== month) {
        month = key;
        items.push({
          type: 'label',
          label: `${key.slice(0, 4)}. ${months[Number(key.slice(5, 7)) - 1] ?? ''}`,
        });
      }
      const change = changeLabel(t, day.changes);
      items.push({
        /* ⚠️ The mockup's own `fmtD(d) + ' · ' + DOW[dow(d)]` — *júl 30. · csü*. A wire date
           here would be correct and unreadable at a glance, which is what a list of two hundred
           days is scanned for. */
        label: `${shortDate(t, day.onDate)} · ${weekday(t, day.onDate)}`,
        hint: hint(day),
        checked: day.onDate === lookback.onDate,
        /* ⚠️ The mockup's own amber, and the reason the list is worth opening: it is where the
           plan moved, not just where the hours went up. */
        ...(change === null ? {} : { dot: 'var(--status-warn)', description: change }),
        onSelect: () => {
          lookback.go(day.onDate);
        },
      });
    }
    return items;
  }, [t, lookback, hint]);
}

/** ⚠️ `júl 30.` — the mockup's `fmtD`, from the catalogue rather than a locale API. */
function shortDate(t: Translate, onDate: string): string {
  const months = t('calendar.monthsAbbr').split(',');
  return `${months[Number(onDate.slice(5, 7)) - 1] ?? ''} ${String(Number(onDate.slice(8)))}.`;
}

/** ⚠️ Three letters, from the catalogue — `hét`, `csü`, never a `toLocaleDateString`. */
function weekday(t: Translate, onDate: string): string {
  const names = t('calendar.weekdaysAbbr').split(',');
  /* ⚠️ `getUTCDay` is 0 for Sunday; the catalogue's list starts on Monday, as the calendar does. */
  const index = (new Date(`${onDate}T00:00:00.000Z`).getUTCDay() + 6) % 7;
  return names[index] ?? '';
}
